import React, { useEffect, useState } from "react";
import { Bell, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getOrCreateClientId, getPushEnabled, isPushSupported, subscribeToPush } from "../lib/pushClient";

let _follows = null; // session cache of followed incident ids

async function loadFollows() {
  if (_follows) return _follows;
  const cid = getOrCreateClientId();
  const r = await api.get(`/push/follows/${cid}`);
  _follows = new Set(r.data.incident_ids || []);
  return _follows;
}

export default function FollowFireButton({ incidentId, compact = false, onChange }) {
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    loadFollows().then((s) => alive && setFollowing(s.has(incidentId))).catch(() => {});
    return () => { alive = false; };
  }, [incidentId]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const cid = getOrCreateClientId();
      if (!following) {
        // Follow always works — push notifications are a bonus, not a requirement.
        let pushOk = getPushEnabled();
        if (!pushOk && isPushSupported()) {
          try {
            await subscribeToPush([]);
            pushOk = true;
          } catch {
            pushOk = false;
          }
        }
        await api.post("/push/follow", { client_id: cid, incident_id: incidentId });
        _follows?.add(incidentId);
        setFollowing(true);
        onChange?.(true);
        if (pushOk) {
          toast.success("Following this fire — you'll get status & acreage updates.");
        } else {
          toast.success(
            "Following — it's now pinned on your Live Map. Enable notifications to also get push updates.",
          );
        }
      } else {
        await api.post("/push/unfollow", { client_id: cid, incident_id: incidentId });
        _follows?.delete(incidentId);
        setFollowing(false);
        onChange?.(false);
        toast("Unfollowed — no more updates for this fire.");
      }
    } catch {
      toast.error("Action failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid={`follow-fire-btn-${incidentId}`}
        title={following ? "Unfollow this fire" : "Follow this fire — get push updates"}
        className={`p-1.5 rounded-sm border transition-colors ${
          following
            ? "border-tac-primary/60 text-tac-primary bg-tac-primary/10"
            : "border-tac-border text-tac-faint hover:text-tac-text hover:border-tac-borderBright"
        }`}
      >
        {following ? <BellRinging size={13} weight="fill" /> : <Bell size={13} weight="bold" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`follow-fire-btn-${incidentId}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm transition-colors disabled:opacity-50 ${
        following
          ? "border-tac-primary/60 text-tac-primary bg-tac-primary/10"
          : "border-tac-border text-tac-dim hover:text-tac-text hover:border-tac-borderBright"
      }`}
    >
      {following ? <BellRinging size={12} weight="fill" /> : <Bell size={12} weight="bold" />}
      {following ? "Following" : "Follow this fire"}
    </button>
  );
}
